import React, { createContext, useEffect, useState } from 'react'
import { CGT_TYPES } from '@constants/cgtTypes'
import { Outlet, useSearchParams, } from 'react-router-dom'
import IDonor from '@interfaces/IDonor'
import IPagination from '@interfaces/IPagination'
import * as $Donor from '@services/Donor'
import { deleteAllSearchParams, formDataReduce, setEntriesToSearchParams } from '@helpers/Filters'
import { IFilter } from '@interfaces/IFilter'

interface IMinMaxValues {
  min_height: number
  max_height: number
  min_weight: number
  max_weight: number
  min_sample_value: number
  max_sample_value: number
}

type DonorsContextProps = {
  isLoading: boolean
  donors: IDonor[]
  setDonors: React.Dispatch<React.SetStateAction<IDonor[]>>
  compatibleDonors: IDonor[]
  bannerState: 'offer' | 'info' | 'none'
  pagination: IPagination
  searchDonors: (searchParams: URLSearchParams) => void
  setPage: (page: number) => void
  options: IFilter
  setOptions: React.Dispatch<React.SetStateAction<IFilter>>
  filters: IFilter
  setFilters: React.Dispatch<React.SetStateAction<IFilter>>
  selectedFilters: IFilter
  setSelectedFilters: React.Dispatch<React.SetStateAction<IFilter>>
  minMaxValues: IMinMaxValues
  handleOnSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  handleNextPage: () => void
  handleApplyFilters: () => void
  handleClearFilters: () => void
  handleClearSelectedFilters: (filterType?: string) => void
}

const DonorsContext = createContext<DonorsContextProps>({} as DonorsContextProps)

export const DonorsProvider: React.FC = () => {
  const [ donors, setDonors ] = useState<IDonor[]>([])
  const [ compatibleDonors, setCompatibleDonors ] = useState<IDonor[]>([])
  const [ bannerState, setBannerState ] = useState<'offer' | 'info' | 'none'>('none')

  const [ isLoading, setIsLoading ] = useState<boolean>(true)
  const [ pagination, setPagination ] = useState<IPagination>({
    per_page: 15,
    current_page: 1,
    last_page: 1,
    total: 0,
  })

  const filterDefaultObject = {
    hasFilter: false,

    internalId: '',

    skinColors: [],
    exposedToSunSkinColors: [],
    faceShapes: [],
    noseShapes: [],
    spermBanks: [],
    treatmentTypes: [],
    eyeColors: [],
    eyebrows: [],
    distanceBetweenEyes: [],
    hairTypes: [],
    hairColors: [],
    races: [],
    bloodGroups: [],
    boneStructures: [],
    eyeShapes: [],
    lips: [],
    teethTypes: [],
    particularities: [],
    donorTypes: [],
    geneticMappings: [],
    newDonors: [],
    donorsWithPhotos: [],
    heightMin: 0,
    heightMax: 0,
    weightMin: 0,
    weightMax: 0,
    sampleValueMin: 0,
    sampleValueMax: 0,
  }

  const [options, setOptions] = useState<IFilter>(filterDefaultObject)

  const [filters, setFilters] = useState<IFilter>(filterDefaultObject)
  const [selectedFilters, setSelectedFilters] = useState<IFilter>(filterDefaultObject)
  const [ minMaxValues, setMinMaxValues ] = useState<IMinMaxValues>({} as IMinMaxValues)
  const [isFetchingNextPage, setIsFetchingNextPage] = useState<boolean>(false)

  const [ searchParams, setSearchParams ] = useSearchParams()

  // Asymmetric rule: an Inseminação (IIU) sample is also usable for ICSI/FIV, so
  // when the filter contains ICSI/FIV (and not Inseminação) we offer the IIU-only
  // donors in a banner at the end of the list. Not applicable otherwise.
  const bannerRuleApplies = (sp: URLSearchParams) => {
    const raw = [...sp.getAll('treatmentTypes'), ...sp.getAll('treatmentTypes[]')]
      .flatMap(value => value.includes(',') ? value.split(',') : [value])
      .map(value => value.toLowerCase())

    const hasIcsiOrFiv = raw.some(type => type === 'icsi' || type === 'fiv')
    const hasInseminacao = raw.some(type => type === 'iiu' || type === 'inseminação')

    return hasIcsiOrFiv && !hasInseminacao
  }

  const fetchCompatibleDonors = (searchParams: URLSearchParams) => {
    if (!bannerRuleApplies(searchParams)) {
      setCompatibleDonors([])
      setBannerState('none')
      return
    }

    $Donor.compatible(searchParams).then(({ data: { data } }: any) => {
      setCompatibleDonors(data)
      setBannerState(data.length > 0 ? 'offer' : 'info')
    }).catch(() => {
      setCompatibleDonors([])
      setBannerState('none')
    })
  }

  const searchDonors = (searchParams: URLSearchParams, append = false) => {
    setSearchParams(searchParams)
    setIsLoading(true)

    $Donor.all(searchParams).then(({ data: { data: newDonors, ...pagination } }: any) => {
      setDonors(prevDonors => append ? [...prevDonors, ...newDonors] : newDonors)
      setPagination(pagination)
    }).finally(() => {
      setIsLoading(false)
      setIsFetchingNextPage(false)
    })

    // The banner list is independent of pagination — fetch it only on fresh searches.
    if (!append) {
      fetchCompatibleDonors(searchParams)
    }
  }

  const setPage = (page: number) => {
    searchParams.set('page', page.toString())
    searchDonors(searchParams, true)
  }

  const checkIfHasFilters = (filters: IFilter) => {
    return Object.keys(filters).some(key => {
      if (key === 'hasFilter' || key === 'page' || key === 'login' || key === 'verified') return false

      if (Array.isArray(filters[key])) {
        return filters[key].length > 0
      }

      if (typeof filters[key] === 'object') {
        return filters[key].minValue > 0 || filters[key].maxValue > 0
      }

      return filters[key] !== '' && filters[key] !== 0 && filters[key] !== '0'
    })
  }

  useEffect(() => {
    const savedPage = parseInt(searchParams.get('page') || '1', 10)

    if (savedPage > 1) {
      // Reload scenario: fetch all donors up to the saved page in a single request
      searchParams.set('page', '1')
      searchParams.set('per_page', String(savedPage * 10))
      setSearchParams(searchParams)
      setIsLoading(true)

      $Donor.all(searchParams).then(({ data: { data: newDonors, ...pag } }: any) => {
        setDonors(newDonors)
        // Restore pagination as if user had scrolled to savedPage with per_page=10
        setPagination({
          per_page: 10,
          current_page: savedPage,
          last_page: Math.ceil(pag.total / 10),
          total: pag.total,
        })
        // Restore URL to reflect the page the user was on
        searchParams.set('page', String(savedPage))
        searchParams.delete('per_page')
        setSearchParams(searchParams)
      }).finally(() => {
        setIsLoading(false)
      })

      fetchCompatibleDonors(searchParams)
    } else {
      searchParams.delete('page')
      searchDonors(searchParams)
    }

    setIsLoading(true)

    $Donor.characteristics().then(({ data }) => {
      const cb = ({ id, name }: any) => ({ key: id, value: name })

      const { min_height, max_height, min_weight, max_weight, min_sample_value, max_sample_value } = data

      setMinMaxValues({
        min_height,
        max_height,
        min_weight,
        max_weight,
        min_sample_value,
        max_sample_value,
      })

      const skinColors = data.skin_colors.map(cb)
      const exposedToSunSkinColors = data.exposed_to_sun_skin_colors.map(cb)
      const faceShapes = data.face_shapes.map(cb)
      const noseShapes = data.nose_shapes.map(cb)
      const spermBanks = data.sperm_banks.map(cb)
      const treatmentTypes = data.treatment_types.map(({ name }: any) => ({ key: String(name).toLowerCase(), value: name }))
      const eyeColors = data.eye_colors.map(cb)
      const eyebrows = data.eyebrows.map(cb)
      const hairTypes = data.hair_types.map(cb)
      const hairColors = data.hair_colors.map(cb)
      const races = data.races.map(cb)
      const bloodGroups = data.blood_groups.map(cb)
      const boneStructures = data.bone_structures.map(cb)
      const eyeShapes = data.eye_shapes.map(cb)
      const lips = data.lips.map(cb)
      const teethTypes = data.teeth_types.map(cb)
      const distanceBetweenEyes = data.distance_between_eyes.map(cb)
      const particularities = data.particularities.map(cb)
      const donorTypes = ['ID Release', 'No ID Release']
      const geneticMappings = CGT_TYPES.map((c: any) => ({ key: c.value, value: c.label }))
      const newDonors = ['new-donors']

      setOptions({
        hasFilter: checkIfHasFilters(filters),
        internalId: '',
        skinColors,
        exposedToSunSkinColors,
        faceShapes,
        noseShapes,
        spermBanks,
        treatmentTypes,
        eyeColors,
        eyebrows,
        distanceBetweenEyes,
        hairTypes,
        hairColors,
        races,
        bloodGroups,
        boneStructures,
        eyeShapes,
        lips,
        teethTypes,
        particularities,
        donorTypes,
        geneticMappings,
        newDonors,
        donorsWithPhotos: [],
        heightMin: 0,
        heightMax: 0,
        weightMin: 0,
        weightMax: 0,
        sampleValueMin: 0,
        sampleValueMax: 0,
      })
    })

    // Build data from searchParams preserving multiple values for same key
    const data: any = {}
    Array.from(searchParams.keys()).forEach((rawKey) => {
      // normalize keys like 'geneticMappings[]' to 'geneticMappings'
      const key = rawKey.endsWith('[]') ? rawKey.slice(0, -2) : rawKey
      const values = searchParams.getAll(rawKey)

      if (values.length === 1 && typeof values[0] === 'string' && values[0].includes(',')) {
        data[key] = values[0].split(',')
      } else {
        data[key] = values
      }
    })

    const hasFilter = checkIfHasFilters(data)

    data.hasFilter = hasFilter

    setFilters(prev => ({ ...prev, ...data }))
    setSelectedFilters(prev => ({ ...prev, ...data }))

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setSelectedFilters(filters)
  }, [filters])

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const data = formDataReduce(new FormData(e.currentTarget))

    setEntriesToSearchParams(searchParams, data)

    searchDonors(searchParams)
  }

  const handleNextPage = () => {
    if (!isLoading && !isFetchingNextPage && pagination.current_page < pagination.last_page) {
      setIsFetchingNextPage(true)
      setPage(pagination.current_page + 1)
      setTimeout(() => {
        setIsFetchingNextPage(false)
      }, 2000)
    }
  }

  const handleApplyFilters = (payload?: IFilter) => {
    const filtersToApply = payload || selectedFilters

    const hasFilter = checkIfHasFilters(filtersToApply)

    const filtersData = {
      ...filtersToApply,
      hasFilter,
    }

    setFilters(filtersData)

    setEntriesToSearchParams(searchParams, filtersData)

    searchDonors(searchParams)
  }

  const handleClearFilters = () => {
    setFilters(filterDefaultObject)

    deleteAllSearchParams(searchParams)

    searchDonors(searchParams)
  }

  const handleClearSelectedFilters = (filterType?: string) => {
    if (filterType) {
      let filtersData = { ...selectedFilters }

      if (filterType === 'all') {
        return handleClearFilters()
      } else if (filterType === 'heightMin') {
        filtersData = {
          ...selectedFilters,
          heightMin: 0,
          heightMax: 0,
        }
      } else if (filterType === 'weightMin') {
        filtersData = {
          ...selectedFilters,
          weightMin: 0,
          weightMax: 0,
        }
      } else if (filterType === 'sampleValue' || filterType === 'sampleValueMin') {
        filtersData = {
          ...selectedFilters,
          sampleValueMin: 0,
          sampleValueMax: 0,
        }
      } else {
        filtersData = {
          ...selectedFilters,
          [filterType]: []
        }
      }

      filtersData = {
        ...filtersData,
        hasFilter: checkIfHasFilters(filtersData)
      }

      setSelectedFilters(filtersData)

      handleApplyFilters(filtersData)
    } else {
      setSelectedFilters(filters)
    }
  }

  return (
    <DonorsContext.Provider
      value={{
        isLoading,
        donors,
        setDonors,
        compatibleDonors,
        bannerState,
        pagination,
        searchDonors,
        setPage,
        options,
        setOptions,
        filters,
        setFilters,
        selectedFilters,
        setSelectedFilters,
        minMaxValues,
        handleOnSubmit,
        handleNextPage,
        handleApplyFilters,
        handleClearFilters,
        handleClearSelectedFilters,
      }}
    >
      <Outlet />
    </DonorsContext.Provider>

  )
}

export default DonorsContext
